// Función auxiliar para tiempo relativo
const getRelativeTime = (date) => {
  if (!date) return '';
  
  const now = new Date();
  const diffInMs = now - new Date(date);
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);
  
  if (diffInMinutes < 1) return 'Ahora';
  if (diffInMinutes < 60) return `Hace ${diffInMinutes} minutos`;
  if (diffInHours < 24) return `Hace ${diffInHours} horas`;
  if (diffInDays < 7) return `Hace ${diffInDays} días`;
  if (diffInWeeks < 4) return `Hace ${diffInWeeks} semanas`;
  if (diffInMonths < 12) return `Hace ${diffInMonths} meses`;
  
  const diffInYears = Math.floor(diffInMonths / 12);
  return `Hace ${diffInYears} años`;
};

// Utilidades para fechas
export const dateUtils = {
  // Formatear fecha en formato chileno
  formatDate: (date, format = 'dd/MM/yyyy') => {
    if (!date) return '';
    
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    
    switch (format) {
      case 'dd/MM/yyyy':
        return `${day}/${month}/${year}`;
      case 'dd/MM/yyyy HH:mm':
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      case 'MM/yyyy':
        return `${month}/${year}`;
      case 'yyyy-MM-dd':
        return `${year}-${month}-${day}`;
      case 'relative':
        return getRelativeTime(d);
      default:
        return d.toLocaleDateString('es-CL');
    }
  },

  // Obtener tiempo relativo en español
  getRelativeTime: getRelativeTime,

  // Verificar si una fecha está vencida
  isOverdue: (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  },

  // Obtener días entre dos fechas
  getDaysBetween: (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffInMs = end - start;
    return Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
  },

  // Verificar si dos fechas están en el mismo día
  isSameDay: (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getDate() === d2.getDate() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getFullYear() === d2.getFullYear();
  },

  // Obtener el inicio del día
  startOfDay: (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  },

  // Obtener el fin del día
  endOfDay: (date) => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
  },

  // Agregar días a una fecha
  addDays: (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  },

  // Obtener nombre del mes en español
  getMonthName: (monthIndex) => {
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return months[monthIndex];
  },

  // Obtener nombre del día en español
  getDayName: (dayIndex) => {
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    return days[dayIndex];
  }
};

// Utilidades para números y moneda
export const numberUtils = {
  // Formatear número como moneda chilena
  formatCurrency: (amount, showSymbol = true) => {
    if (amount === null || amount === undefined) return '$0';
    
    const formatted = new Intl.NumberFormat('es-CL').format(amount);
    return showSymbol ? `$${formatted}` : formatted;
  },

  // Formatear porcentaje
  formatPercentage: (value, decimals = 1) => {
    if (value === null || value === undefined) return '0%';
    return `${parseFloat(value).toFixed(decimals)}%`;
  },

  // Formatear número con separadores de miles
  formatNumber: (number, decimals = 0) => {
    if (number === null || number === undefined) return '0';
    return parseFloat(number).toLocaleString('es-CL', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  },

  // Redondear a cierto número de decimales
  roundTo: (number, decimals = 2) => {
    if (number === null || number === undefined) return 0;
    return Math.round(number * Math.pow(10, decimals)) / Math.pow(10, decimals);
  },

  // Generar número aleatorio entre min y max
  random: (min, max) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  },

  // Validar si es un número válido
  isValidNumber: (value) => {
    return !isNaN(value) && !isNaN(parseFloat(value));
  }
};

// Utilidades para strings
export const stringUtils = {
  // Capitalizar primera letra
  capitalize: (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  },

  // Capitalizar cada palabra
  titleCase: (str) => {
    if (!str) return '';
    return str.split(' ')
              .map(word => stringUtils.capitalize(word))
              .join(' ');
  },

  // Truncar texto con puntos suspensivos
  truncate: (str, maxLength, suffix = '...') => {
    if (!str || str.length <= maxLength) return str;
    return str.substring(0, maxLength - suffix.length) + suffix;
  },

  // Quitar acentos y caracteres especiales
  removeAccents: (str) => {
    if (!str) return '';
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  },

  // Convertir a slug (URL friendly)
  slugify: (str) => {
    if (!str) return '';
    return stringUtils.removeAccents(str)
                     .toLowerCase()
                     .replace(/[^a-z0-9\s-]/g, '')
                     .replace(/\s+/g, '-')
                     .replace(/-+/g, '-')
                     .trim('-');
  },

  // Generar iniciales
  getInitials: (name) => {
    if (!name) return '';
    return name.split(' ')
               .map(word => word.charAt(0).toUpperCase())
               .join('')
               .substring(0, 2);
  },

  // Validar email
  isValidEmail: (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  // Validar RUT chileno
  isValidRUT: (rut) => {
    if (!rut) return false;
    
    // Limpiar RUT
    const cleanRUT = rut.replace(/[^0-9kK]/g, '');
    if (cleanRUT.length < 8 || cleanRUT.length > 9) return false;
    
    const body = cleanRUT.slice(0, -1);
    const dv = cleanRUT.slice(-1).toLowerCase();
    
    let sum = 0;
    let multiplier = 2;
    
    for (let i = body.length - 1; i >= 0; i--) {
      sum += parseInt(body.charAt(i)) * multiplier;
      multiplier = multiplier === 7 ? 2 : multiplier + 1;
    }
    
    const calculatedDV = 11 - (sum % 11);
    let expectedDV;
    
    if (calculatedDV === 11) expectedDV = '0';
    else if (calculatedDV === 10) expectedDV = 'k';
    else expectedDV = calculatedDV.toString();
    
    return dv === expectedDV;
  },

  // Formatear RUT
  formatRUT: (rut) => {
    if (!rut) return '';
    
    const cleanRUT = rut.replace(/[^0-9kK]/g, '');
    if (cleanRUT.length < 8) return rut;
    
    const body = cleanRUT.slice(0, -1);
    const dv = cleanRUT.slice(-1);
    
    return `${body.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${dv}`;
  },

  // Generar color aleatorio
  generateColor: () => {
    const colors = [
      '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444',
      '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6366F1'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }
};

// Utilidades para validaciones
export const validationUtils = {
  // Reglas de validación comunes
  required: (value, message = 'Este campo es requerido') => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      return message;
    }
    return null;
  },

  email: (value, message = 'Ingresa un email válido') => {
    if (value && !stringUtils.isValidEmail(value)) {
      return message;
    }
    return null;
  },

  rut: (value, message = 'Ingresa un RUT válido') => {
    if (value && !stringUtils.isValidRUT(value)) {
      return message;
    }
    return null;
  }
};

// Exportar todas las utilidades
export const utils = {
  date: dateUtils,
  number: numberUtils,
  string: stringUtils,
  validation: validationUtils
};